// "Which league does this URL belong to?" — the answer, with no dependencies.
//
// This lives on its own rather than inside useLeagueContext because the API
// layer needs it too, and useLeagueContext imports from the API layer. Keeping
// the pure path logic here breaks that cycle, and keeps ONE definition of the
// route lists: getting these two sets out of step with App.jsx is exactly what
// silently threw members out of their league once already.

// Screens under /contest that are NOT a league: the create flow, the invite gate
// and the list pages.
export const CONTEST_NON_LEAGUE = new Set([
  "my-games",
  "find-my-games",
  "add-league",
  "cash-leagues",
  "public-leagues",
  "find-games",
  "create-public-league",
  "create-private-league",
  "private-league-created",
  "public-league-created",
  "private-league-preview",
  "private-league-joined",
  "join",
]);

// Per-league routes that live at the root: /<slug>/trade, /<slug>/portfolio …
//
// This set must stay in step with App.jsx's /:slug/* routes. As of now those
// are: buysell, charts, chatHistory, create-feed, edit-feed, feeds, followers,
// leaderboard, portfolio, profile, trade, watchlist — all twelve are listed
// here. Adding a /:slug/ route without adding it here reintroduces the bug where
// the screen still renders but stops being part of the league.
export const LEAGUE_SUB_ROUTES = new Set([
  "feeds",
  "buysell",
  "trade",
  "charts",
  "portfolio",
  "leaderboard",
  "create-feed",
  "edit-feed",
  "watchlist",
  "profile",
  "chatHistory",
  "followers",
]);

export const leagueSlugFromPath = (pathname) => {
  const parts = (pathname || "").split("/");

  if (parts[1] === "contest" && parts[2] && !CONTEST_NON_LEAGUE.has(parts[2])) {
    // /contest/<slug> and its own sub-screens (portfolio, details, settings).
    return parts[2];
  }
  if (parts[1] && parts[2] && LEAGUE_SUB_ROUTES.has(parts[2])) {
    return parts[1];
  }
  return null;
};

// Notifications and Chats opened from INSIDE a league (client Rev, desktop:
// "open the same existing Notifications or Chats content while preserving the
// current League shell and current league ID"). They render at
// /contest/<slug>/notifications and /contest/<slug>/chat — the SAME components
// the main app mounts at /notifications and /chat, only at a different address.
//
// Three places have to recognise that address: the two screens themselves (which
// must not render a second DashboardHeader / PageContainer on top of the ones
// the Contest layout already provides) and DashboardHeader (which decides which
// glyph belongs in the top right). Exact-path tests rather than
// `leagueSlugFromPath`, because the rule is about these two screens only and
// must never catch the main app's own /notifications and /chat.
export const isLeagueNotificationsPath = (pathname) =>
  /^\/contest\/[^/]+\/notifications$/.test(pathname || "");

export const isLeagueChatPath = (pathname) =>
  /^\/contest\/[^/]+\/chat$/.test(pathname || "");

// One DIRECT MESSAGE conversation, at either of its two addresses:
//
//   /chatHistory/<id>[/<group id>]     opened from the main app
//   /<slug>/chatHistory/<id>           opened from inside a league
//
// Both render the SAME screen now (client: "make the direct message experience
// consistent everywhere, regardless of whether the user enters from the main
// QUSHi site or from inside League Play"), so the places that have to know a
// conversation is open ask this one question instead of testing one spelling and
// silently missing the other. Two of them mattered: the bottom nav, which would
// otherwise sit on top of the composer inside a league, and the header's Chats
// glyph, which pointed at a screen the user was already on.
//
// Deliberately does NOT match /contest/<slug>/chat - that is the Chats HOME
// screen hosted in a league, which is a different screen and is left alone.
export const isDirectMessagePath = (pathname) => {
  const parts = (pathname || "").split("/");
  if (parts[1] === "chatHistory") return true;
  return !!parts[1] && parts[2] === "chatHistory";
};

// ── The standalone leaderboard, and the profiles reached from it ──────────
//
// Client, League Play Dev Request: "'View Results' CTA from any completed card
// must open the final leaderboard directly - not take people into the league
// home or the league shell. 'View Leaderboard' CTA from a live public league
// must open the live leaderboard directly - outside the league shell. These
// leaderboard views must remain inside the main QUSHi site and must not open the
// league shell."
//
// The existing league leaderboard lives at /<slug>/leaderboard, and that address
// is in LEAGUE_SUB_ROUTES — which is precisely what tells the app shell "you are
// standing inside this league", swaps the left rail for the league's own nav and
// wraps the page in LeagueShell. So the standalone view cannot be that URL; it
// needs one leagueSlugFromPath does NOT resolve, which is what /leaderboards/…
// is. Nothing here is a second leaderboard: the same LeaderboardSecton renders in
// both places (see LeagueLeaderboardStandalone), only without the league chrome.
//
// The trailing /profile/<id> is the same rule for the league stats profile the
// podium and rows open: reached from the standalone board, it must stay outside
// the shell too.
// The leaderboard PREVIEW address — /leaderboards/<slug>, and the details
// screen reached from it.
//
// Client, League Play header standardization: "Leaderboard preview: Remove the
// avatar from the top-left. Put the standard back arrow in the avatar's place,
// top-left in the header. Keep the QUSHi logo centered. No share icon."
//
// DashboardHeader decides its left slot from a list of paths, and this address
// was in none of them — which is why it fell through to the default avatar. It
// is a predicate rather than another regex inlined into that list because the
// screen itself needs the same answer (see LeagueLeaderboardStandalone).
export const isStandaloneLeaderboardPath = (pathname) =>
  /^\/leaderboards\/[^/]+\/?$/.test(pathname || "");

export const standaloneLeaderboardPath = (slug) =>
  slug ? `/leaderboards/${slug}` : "/leaderboard";

// WHERE A COMPETITION LISTING OPENS — one rule for every kind of competition.
//
// Client: "All of league play should carry the same state logic, including cash
// games. We need to stay consistent so the system is easy to learn... Members
// bypass the landing page for Starting Soon or Live. Members completed >
// Leaderboard preview. Non-game members: landing page for Starting Soon, view
// leaderboard (preview) for Live, and View Results (leaderboard preview) for
// Completed. Use the same logic for all league play."
//
// Which collapses to two lines, because membership only changes ONE cell:
//
//                    MEMBER                    NON-MEMBER
//   Starting soon     competition home          landing page
//   Live              leaderboard preview       leaderboard preview
//   Completed         leaderboard preview       leaderboard preview
//
// Live and Completed go to the board for everyone, so no membership test is
// needed for them. Starting soon goes to /contest/<slug>, which IS both
// answers: that route is the competition's front door and already shows a
// member their home and a visitor the landing page (see LeagueEntry). So the
// membership split is handled by the destination, not by this function.
//
// Written once, here, because it had been written FIVE times — PublicLeagueRow,
// CashGameRow and three cash cards each carried their own version, and they did
// not agree. CashGameRow sent every state to the landing page; the two Featured
// cash cards tested `isLive` alone, so a finished game fell through to the
// landing page too. One function means a sixth listing cannot invent a sixth
// answer.
export const competitionDestination = (game, status) => {
  const slug = game?.vanity_url;
  if (!slug) return "/contest";
  const tone = status?.tone;
  if (tone === "live" || tone === "ended") return standaloneLeaderboardPath(slug);
  return `/contest/${slug}`;
};

export const standaloneLeagueProfilePath = (slug, userId) =>
  slug && userId ? `/leaderboards/${slug}/profile/${userId}` : "/profile";

// Where "League Dashboard" goes: the member's OWN league profile — their League
// Record inside this league.
//
// Client, on the desktop left menu: "the League left menu column on desktop has
// the League Dashboard tab near the bottom. That tab currently points to the
// league portfolio screen when clicked. This button should go to the user's
// league profile dashboard (which is this screen)."
//
// And now, on the mobile header's avatar: "inside league play whenever click on
// avatar it will move to league dashboard." Two places, one destination — so the
// rule lives here rather than as a second copy that can drift from the first.
//
// Falls back to the league Portfolio only when the id is missing from storage,
// since /<slug>/profile/ with no id is not a route; that keeps the tap inside the
// league either way rather than dropping it on an error page. Outside a league
// there is no league dashboard, so it is the main profile.
export const leagueDashboardPath = (slug, userId) => {
  if (!slug) return "/profile";
  return userId ? `/${slug}/profile/${userId}` : `/contest/${slug}/portfolio`;
};

export default leagueSlugFromPath;
